import { useRef } from "react";
import {
  Alert,
  Button,
  Chip,
  List,
  ListItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { tournamentApi } from "@bw/shared/api";
import type { TournamentPairingRequestResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import { useRespondPairing } from "../hooks/useRespondPairing";

export function TournamentPairingRequests() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const pairingId = params.get("pairing_id") || undefined;
  const rawPage = Number(params.get("pairing_page"));
  const page =
    !pairingId && Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const requests = useQuery({
    queryKey: [
      ...queryKeys.tournaments.myPairingRequests(),
      user?.uid,
      { page, pairingId },
    ],
    queryFn: () =>
      tournamentApi.getMyPairingRequests({
        limit: 20,
        offset: (page - 1) * 20,
        ...(pairingId ? { pairing_id: pairingId } : {}),
      }),
    enabled: Boolean(user),
  });
  function changePage(value: number) {
    const next = new URLSearchParams(params);
    next.set("pairing_page", String(value));
    setParams(next);
  }
  if (!user) return null;
  // Nothing to act on: stay quiet rather than a heading over an empty line.
  if (
    !pairingId &&
    page === 1 &&
    requests.isSuccess &&
    !requests.data?.items.length
  )
    return null;
  return (
    <Stack
      component="section"
      aria-label="Partner requests"
      spacing={1.5}
      sx={{ mb: 3 }}
    >
      <Typography component="h2" variant="h6">
        Partner requests
      </Typography>
      {pairingId ? (
        <Button
          sx={{ alignSelf: "flex-start" }}
          onClick={() => {
            const next = new URLSearchParams(params);
            next.delete("pairing_id");
            next.delete("pairing_page");
            setParams(next);
          }}
        >
          All requests
        </Button>
      ) : null}
      {requests.isLoading ? (
        <Typography role="status">Loading partner requests…</Typography>
      ) : requests.isError ? (
        <Alert
          severity="error"
          action={
            <Button onClick={() => void requests.refetch()}>Retry</Button>
          }
        >
          Could not load your partner requests.
        </Alert>
      ) : !requests.data?.items.length ? (
        <Typography color="text.secondary">
          {pairingId
            ? "This request is unavailable."
            : "No partner requests on this page."}
        </Typography>
      ) : (
        <List disablePadding component={Paper} variant="outlined">
          {requests.data.items.map((request) => (
            <RequestRow
              key={`${user.uid}:${request.pairing_id}`}
              request={request}
              refreshing={requests.isFetching}
            />
          ))}
        </List>
      )}
      {requests.data &&
      !requests.isError &&
      (page > 1 || requests.data.has_more) ? (
        <Stack direction="row" spacing={1}>
          <Button disabled={page === 1} onClick={() => changePage(page - 1)}>
            Previous requests
          </Button>
          <Button
            disabled={!requests.data.has_more}
            onClick={() => changePage(page + 1)}
          >
            Next requests
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}

function RequestRow({
  request,
  refreshing,
}: {
  request: TournamentPairingRequestResponse;
  refreshing: boolean;
}) {
  const respond = useRespondPairing(request.draw_id, request.tournament_id);
  const lock = useRef(false);
  const incoming = request.viewer_role === "partner";
  const pending = respond.isPending || refreshing;
  async function submit(accept: boolean) {
    if (
      lock.current ||
      pending ||
      !incoming ||
      request.status !== "proposed" ||
      !(accept ? request.can_accept : request.can_decline)
    )
      return;
    lock.current = true;
    try {
      await respond.mutateAsync({ pairingId: request.pairing_id, accept });
    } catch {
      /* Mutation state supplies safe copy; fresh source remains authoritative. */
    } finally {
      lock.current = false;
    }
  }
  return (
    <ListItem divider sx={{ display: "block", py: 2 }}>
      <Stack
        spacing={1}
        sx={{ alignItems: "flex-start", overflowWrap: "anywhere" }}
      >
        <Typography sx={{ fontWeight: 700 }}>
          {request.tournament_name}
        </Typography>
        <Typography variant="body2">
          {request.event_name} · {request.draw_name}
        </Typography>
        <Typography variant="body2">
          {incoming ? "Incoming" : "Outgoing"} ·{" "}
          {incoming
            ? (request.proposer_display_name ?? "Another player")
            : (request.partner_display_name ?? "Another player")}
        </Typography>
        <Chip label={request.status.replaceAll("_", " ")} size="small" />
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          {incoming && request.status === "proposed" && request.can_accept ? (
            <Button disabled={pending} onClick={() => void submit(true)}>
              Accept
            </Button>
          ) : null}
          {incoming && request.status === "proposed" && request.can_decline ? (
            <Button disabled={pending} onClick={() => void submit(false)}>
              Decline
            </Button>
          ) : null}
          <Button component={RouterLink} to={request.detail_url}>
            View request
          </Button>
          {request.entry_url ? (
            <Button component={RouterLink} to={request.entry_url}>
              View entry
            </Button>
          ) : null}
        </Stack>
        {respond.isError ? (
          <Alert severity="error">
            Could not confirm your response. Review the latest request status.
          </Alert>
        ) : null}
      </Stack>
    </ListItem>
  );
}
